import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Wire types — mirror the backend `views::stp_overlay` shapes exactly
// (camelCase on the wire). Field-for-field match, no transformer in
// between.

export interface StpOverlayInstance {
  instanceId: number;
  instanceType: string;
  vlanId: number | null;
  deviceCount: number;
}

export interface StpOverlayDeviceState {
  deviceId: string;
  instanceId: number;
  instanceType: string;
  bridgeId: string;
  rootId: string;
  isRootBridge: boolean;
  /** null: the source reports no count (a UniFi controller states none). Not 0. */
  topologyChanges: number | null;
  lastTopologyChange: string | null;
}

export interface StpOverlayEdgeState {
  connectionId: string;
  sourceDeviceId: string;
  sourceInterfaceId: string;
  targetDeviceId: string;
  targetInterfaceId: string;
  instanceId: number;
  /**
   * Protocol variant — "stp" / "rstp" / "mstp" / "pvst" / "rapid_pvst"
   * / "unknown". Required for selection filtering: the same
   * instance_id (e.g. 0) means different things across variants, so
   * a selector keyed only on instance_id collapses RSTP-0 and
   * MSTP-CIST into one bucket.
   */
  instanceType: string;
  sourcePortState: string | null;
  sourcePortRole: string | null;
  sourceAdminEdgePort: boolean | null;
  sourceBpduGuardViolated: boolean | null;
  sourceRootGuardViolated: boolean | null;
  targetPortState: string | null;
  targetPortRole: string | null;
  targetAdminEdgePort: boolean | null;
  targetBpduGuardViolated: boolean | null;
  targetRootGuardViolated: boolean | null;
}

export interface StpOverlayResponse {
  regionId: string;
  instances: StpOverlayInstance[];
  deviceStates: StpOverlayDeviceState[];
  edgeStates: StpOverlayEdgeState[];
}

/**
 * Fetch the STP overlay data for a region. One request per view load —
 * the all-instances payload lets the legend switch instances client-
 * side without a refetch.
 */
export function useStpOverlay(regionId: string | null | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<StpOverlayResponse>({
    queryKey: ['stpOverlay', regionId],
    queryFn: async () => {
      if (!regionId) throw new Error('Region ID is required');
      const response = await axios.get<StpOverlayResponse>(
        `${config.backendApi}/api/stp-overlay`,
        {
          params: { regionId },
          withCredentials: true,
        }
      );
      return response.data;
    },
    enabled: !!regionId && contextReady,
  });
}

/**
 * Build a human-readable label for an instance. MSTP / PVST+ have
 * structured forms; STP/RSTP collapse to just the variant name since
 * there's only one instance.
 */
export function stpInstanceLabel(instance: StpOverlayInstance): string {
  const type = instance.instanceType.toLowerCase();
  if (type === 'mstp') {
    if (instance.instanceId === 0) return 'MST0 (CIST)';
    return `MST${String(instance.instanceId)}`;
  }
  if (type === 'pvst' || type === 'rapid_pvst') {
    const vlan = instance.vlanId ?? instance.instanceId;
    return `VLAN ${String(vlan)}`;
  }
  // STP / RSTP / unknown: one instance per device, so the variant
  // name alone is enough.
  return type.toUpperCase();
}
